import { membershipTierRank } from '@/utils/memberTier';
import { MemberType } from '@/types/package';

export { MemberType };

/**
 * 会员周期枚举
 * - subscribe: 连续包月（标准版）
 * - halfSubscribe: 连续包半月（专业版）
 * - season: 按季购买
 * - year: 按年购买
 */
export enum MemberCycle {
  SUBSCRIBE = 'subscribe',
  HALF_SUBSCRIBE = 'halfSubscribe',
  /** VVIP 月度买断（非连续订阅） */
  MONTH = 'month',
  /** VVIP 月度连续包月（新购连续订阅） */
  CONTINUOUS_MONTH = 'continuousMonth',
  SEASON = 'season',
  YEAR = 'year'
}

/**
 * 按钮状态枚举
 * - normal: 正常状态（可点击）
 * - disabled: 禁用状态（不可点击）
 * - renew: 续费中状态
 * - upgrade: 升级中状态
 * - needUnbind: 需要先解绑会员，才能进行续费、升级操作
 */
export enum ButtonStatus {
  NORMAL = 'normal',
  DISABLED = 'disabled',
  RENEW = 'renew',
  UPGRADE = 'upgrade',
  NEED_UNBIND = 'needUnbind'
}

/**
 * 按钮文案枚举
 * - renewMember: 续费会员
 * - upgradeMember: 升级会员
 * - cannotDowngrade: 无法降级
 * - alreadySeason: 您已是季度会员
 * - alreadyYear: 您已是年度会员
 * - buyMember: 开通会员
 * - freeTrial: 免费使用
 * - openMember: 立即开通
 */
export enum ButtonText {
  RENEW_MEMBER = '续费会员',
  UPGRADE_MEMBER = '升级会员',
  CANNOT_DOWNGRADE = '无法降级',
  ALREADY_SEASON = '您已是季度会员',
  ALREADY_YEAR = '您已是年度会员',
  ALREADY_PRO = '您已是标准版会员',
  ALREADY_PLUS = '您已是专业版会员',
  ALREADY_ULTRA = '您已是旗舰版会员',
  BUY_MEMBER = '开通会员',
  FREE_TRIAL = '免费使用',
  OPEN_MEMBER = '立即开通'
}

/**
 * 按钮状态接口
 */
export interface ButtonState {
  status: ButtonStatus;
  text: ButtonText;
}

/**
 * 会员按钮判断接口参数
 */
export interface MemberButtonJudgeParams {
  /** 当前会员类型 */
  currentType: MemberType;
  /** 当前会员周期，免费用户为null */
  currentCycle: MemberCycle | null;
  /** 目标会员类型 */
  targetType: MemberType;
  /** 目标会员周期 */
  targetCycle: MemberCycle;
  /** 是否为A组用户 */
  isAUser: boolean;
  /** 是否为订阅用户 */
  isSubscribedUser: boolean;
}

/**
 * 判断会员按钮状态
 * @param params 判断参数
 * @returns 按钮状态对象
 */
function tierRank(type: MemberType): number {
  return membershipTierRank(type === MemberType.FREE ? 'free' : type);
}

/** 当前会员是否为连续订阅周期（含 SVIP 半月 / B 组月度、VVIP 连续包月） */
function isContinuousSubscriptionCycle(cycle: MemberCycle | null): boolean {
  return (
    cycle === MemberCycle.SUBSCRIBE ||
    cycle === MemberCycle.HALF_SUBSCRIBE ||
    cycle === MemberCycle.CONTINUOUS_MONTH
  );
}

function alreadyMemberText(currentType: MemberType): ButtonText {
  if (currentType === MemberType.PRO) return ButtonText.ALREADY_PRO;
  if (currentType === MemberType.PLUS) return ButtonText.ALREADY_PLUS;
  return ButtonText.ALREADY_ULTRA;
}

export function judgeMemberButtonState(params: MemberButtonJudgeParams): ButtonState {
  const { currentType, currentCycle, targetType, targetCycle, isAUser, isSubscribedUser } = params;

  // 旗舰版用户不可降级购买 VIP/SVIP
  if (currentType === MemberType.ULTRA && targetType !== MemberType.ULTRA) {
    return {
      status: ButtonStatus.DISABLED,
      text: ButtonText.CANNOT_DOWNGRADE
    };
  }

  // 先判断用户组
  // A组用户可见按季购买，B组用户不可见
  if (!isAUser && targetCycle === MemberCycle.SEASON) {
    return {
      status: ButtonStatus.DISABLED,
      text: ButtonText.CANNOT_DOWNGRADE
    };
  }

  // 免费用户
  if (targetType === MemberType.FREE && !isAUser) {
    return {
      status: ButtonStatus.DISABLED,
      text: ButtonText.FREE_TRIAL
    };
  }

  if (currentType === MemberType.FREE && isAUser) {
    return {
      status: ButtonStatus.NORMAL,
      text: ButtonText.OPEN_MEMBER
    };
  }

  // 判断是否为降级操作（专业版降到标准版）
  if (currentType === MemberType.PLUS && targetType === MemberType.PRO) {
    return {
      status: ButtonStatus.DISABLED,
      text: ButtonText.CANNOT_DOWNGRADE
    };
  }

  // 目标为旗舰版：自低档位升级；免费用户统一「立即开通」文案
  if (targetType === MemberType.ULTRA && tierRank(currentType) < tierRank(MemberType.ULTRA)) {
    if (currentType === MemberType.FREE) {
      return {
        status: ButtonStatus.NORMAL,
        text: ButtonText.OPEN_MEMBER
      };
    }
    return {
      status: ButtonStatus.UPGRADE,
      text: ButtonText.UPGRADE_MEMBER
    };
  }

  // 当前已是旗舰版：同档续费/已是会员
  if (currentType === MemberType.ULTRA && targetType === MemberType.ULTRA) {
    const isVvipContinuousSub = currentCycle === MemberCycle.CONTINUOUS_MONTH;
    const isTargetContinuousSub = targetCycle === MemberCycle.CONTINUOUS_MONTH;
    // 连续包月旗舰版 + 签约中 → 任意同档目标均已是会员
    if (isVvipContinuousSub && isSubscribedUser) {
      return {
        status: ButtonStatus.DISABLED,
        text: ButtonText.ALREADY_ULTRA
      };
    }
    // 连续包月旗舰版 + 已解约 + 连续包月目标 → 已是会员
    if (isVvipContinuousSub && isTargetContinuousSub) {
      return {
        status: ButtonStatus.DISABLED,
        text: ButtonText.ALREADY_ULTRA
      };
    }
    return {
      status: ButtonStatus.RENEW,
      text: ButtonText.RENEW_MEMBER
    };
  }

  // 同等级判定（FREE→任意 已在前面处理；ULTRA→ULTRA 已在前面处理）
  // 规则 R1-Sub-Eq / R2-NoSub-Eq：按等级匹配，不依赖具体周期
  if (currentType === targetType) {
    const isContinuousSub = isContinuousSubscriptionCycle(currentCycle);
    const isTargetContinuousSub = isContinuousSubscriptionCycle(targetCycle);
    // R1-Sub-Eq：连续订阅 + 签约中 + 同等级（任意目标周期）→ 您已是XX会员（DISABLED）
    if (isContinuousSub && isSubscribedUser) {
      return {
        status: ButtonStatus.DISABLED,
        text: alreadyMemberText(currentType)
      };
    }
    // R1-Sub-Eq 扩展：连续订阅 + 已解约 + 同档连续订阅目标 → 您已是XX会员（会员未过期）
    if (isContinuousSub && isTargetContinuousSub) {
      return {
        status: ButtonStatus.DISABLED,
        text: alreadyMemberText(currentType)
      };
    }
    // R2-NoSub-Eq：买断制 / 跨周期同档 → 续费会员
    return {
      status: ButtonStatus.RENEW,
      text: ButtonText.RENEW_MEMBER
    };
  }

  // 跨等级升级（target tier > current tier；ULTRA 目标 / FREE 当前 已在前面处理）
  // 规则 R1-Sub-Up / R2-NoSub-Up
  if (tierRank(targetType) > tierRank(currentType)) {
    // 特例：标准版连续包月 + 签约中 → 升级专业版需要先解绑（NEED_UNBIND）
    if (
      currentType === MemberType.PRO &&
      currentCycle === MemberCycle.SUBSCRIBE &&
      isSubscribedUser &&
      targetType === MemberType.PLUS
    ) {
      return {
        status: ButtonStatus.NEED_UNBIND,
        text: ButtonText.UPGRADE_MEMBER
      };
    }
    return {
      status: ButtonStatus.UPGRADE,
      text: ButtonText.UPGRADE_MEMBER
    };
  }

  // 兜底降级（理论上不可达：PLUS→PRO 与 ULTRA→其它 已在前面拦截）
  // 规则 R1-Sub-Dn / R2-NoSub-Dn
  return {
    status: ButtonStatus.DISABLED,
    text: ButtonText.CANNOT_DOWNGRADE
  };
}
